import { useEffect } from "react";
import { toast as sonner } from "sonner";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { sessionBus } from "@/lib/session-bus";

/**
 * Pencil component `JcV2a` (`v2/Toast/Success`).
 *
 * Backed by [sonner](https://sonner.emilkowal.ski/), wrapped in a thin
 * adapter that preserves the previous `useToast()` API shape so existing
 * call sites (~50 across the app) don't change. The legacy custom
 * `<Toaster>` wrapped its children — we keep that signature too: it
 * renders sonner's Toaster + the SessionExpired bridge alongside
 * `children`.
 */

export type ToastTone = "success" | "error" | "info" | "warning";

type PushArgs = {
  tone?: ToastTone;
  title: string;
  description?: string;
  duration?: number;
};

type ToastApi = {
  push: (t: PushArgs) => string;
  dismiss: (id: string | number) => void;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
};

/** Stateless API — same shape as before, but every call routes through
 *  sonner's global toast manager. */
export const toast: ToastApi = {
  push: ({ tone = "info", title, description, duration }) =>
    String(sonner[tone](title, { description, duration })),
  dismiss: (id) => sonner.dismiss(id),
  success: (title, description) =>
    String(sonner.success(title, { description })),
  error: (title, description) =>
    String(sonner.error(title, { description, duration: 6000 })),
  info: (title, description) => String(sonner.info(title, { description })),
  warning: (title, description) =>
    String(sonner.warning(title, { description })),
};

/** Sonner is global — `useToast()` returns the same constant API every
 *  render. Keeping the hook signature so existing call sites compile. */
export function useToast(): ToastApi {
  return toast;
}

/**
 * Mount once at the app root. Renders sonner's `<Toaster />` + the
 * SessionExpiredListener bridge alongside `children`. Preserves the
 * existing `<Toaster>{children}</Toaster>` mount pattern in `main.tsx`.
 */
export function Toaster({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SessionExpiredListener />
      {children}
      <SonnerToaster position="bottom-right" richColors closeButton />
    </>
  );
}

/**
 * Bridges the module-level `sessionBus` to a sonner toast. When the api
 * middleware (or `authFetch`) detects a 401 + failed refresh, it emits
 * `"expired"` and we surface a warning before the page navigates to the
 * sign-in subapp.
 */
function SessionExpiredListener() {
  useEffect(() => {
    return sessionBus.subscribe((event) => {
      if (event === "expired") {
        toast.warning(
          "Session expired",
          "Please sign in again to continue. We'll bring you right back here.",
        );
      }
    });
  }, []);
  return null;
}
